// Design tokens da lista de Canais.
// Referência: .md/DESIGN_UIUX.md (Painel de Configurações) e
// CanaisDetails.design.js (implementação irmã, tela de detalhe do canal).
//
// Linguagem visual:
// - Neutros zinc (#18181b → #a1a1aa) sobre fundo #f4f4f7; roxo
//   (#6d28d9) SÓ como acento — ação primária, seleção, links.
// - Cor semântica comunica estado (verde = conectado, âmbar = desconectado,
//   vermelho = ação destrutiva); nunca decora.
// - Largura total da área principal (full-bleed) — sem card-shell / max-width.
// - Header sticky flat (branco + border-b), sem radius.
// - Hierarquia por cabeçalho de seção (rótulo uppercase fora do cartão).

export const CANAIS_COMPONENT_DESIGN = {
    // ---------- shell ----------
    root: 'flex flex-col min-h-full w-full bg-[#f4f4f7] text-[#18181b] animate-in fade-in slide-in-from-bottom-4 duration-500',
    fontStack: "'Plus Jakarta Sans', Manrope, ui-sans-serif, system-ui, sans-serif",
    container: 'w-full px-4 md:px-6 lg:px-8',
    pageBody: 'pb-[120px]',

    // ---------- barra de topo (sticky) ----------
    stickyHeader: 'sticky top-0 z-30 bg-white border-b border-[#e7e7ec]',
    topbar: 'flex flex-col md:flex-row md:items-center justify-between gap-4 py-[18px]',
    topbarIdentity: 'flex items-start gap-3.5 min-w-0 flex-1',
    title: 'text-[20px] font-bold tracking-[-0.02em] leading-tight text-[#18181b]',
    subtitle: 'mt-[5px] text-[13px] text-[#71717a] leading-[1.5] max-w-2xl',
    topbarActions: 'flex items-center gap-3.5 flex-shrink-0 flex-wrap md:justify-end',

    planUsage: 'hidden sm:flex items-center gap-2.5',
    planUsageText: 'text-[13px] text-[#71717a] whitespace-nowrap',
    planUsageBar: 'w-24 h-1.5 rounded-full bg-[#f4f4f5] overflow-hidden',
    planUsageFill: 'h-full rounded-full transition-all',
    planUsageFillOk: 'bg-[#6d28d9]',
    planUsageFillDanger: 'bg-[#b42318]',

    // ---------- cabeçalho de seção (fora do cartão) ----------
    sectionHeader: 'mt-[18px] first:mt-0 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3',
    sectionHeaderTitle: 'text-[13px] font-bold uppercase tracking-[0.06em] text-[#a1a1aa]',
    sectionHeaderSub: 'mt-1.5 text-[13px] text-[#71717a] leading-[1.5]',

    // ---------- filtros ----------
    filtersRow: 'flex flex-col lg:flex-row lg:items-center gap-2.5 mt-4 mb-5',
    searchWrap: 'relative flex-1 min-w-0',
    searchIcon: 'absolute left-3.5 top-1/2 -translate-y-1/2 text-[#a1a1aa] pointer-events-none',
    search:
        'w-full h-10 box-border pl-10 pr-3.5 rounded-lg border border-[#d4d4d8] bg-white text-[14px] text-[#18181b] placeholder:text-[#a1a1aa] shadow-none transition-colors focus:outline-none focus:border-[#8b5cf6] focus:ring-2 focus:ring-[#c4b5fd]',
    filterGroup: 'grid grid-cols-2 sm:flex sm:items-center gap-2',
    select:
        'h-10 min-w-0 sm:min-w-[160px] px-3.5 rounded-lg border-[#d4d4d8] bg-white text-[14px] text-[#18181b] shadow-none transition-colors hover:border-[#b8b8c0] focus:border-[#8b5cf6] focus:ring-2 focus:ring-[#c4b5fd]',
    viewToggleWrap: 'h-10 inline-flex items-center gap-0.5 bg-[#f4f4f5] p-1 rounded-lg col-span-2 sm:col-span-1',
    viewToggleBtn:
        'h-8 px-3 rounded-md text-[12.5px] font-semibold text-[#71717a] transition-colors data-[state=active]:bg-white data-[state=active]:text-[#18181b] data-[state=active]:shadow-sm',

    actionButton:
        'h-10 inline-flex items-center justify-center gap-2 px-4 rounded-[10px] bg-gradient-to-b from-[#8b5cf6] to-[#7c3aed] text-white text-[14px] font-semibold whitespace-nowrap shadow-[0_2px_8px_rgba(124,58,237,.32)] transition-colors hover:from-[#7c3aed] hover:to-[#6d28d9] disabled:from-[#e4e4e7] disabled:to-[#e4e4e7] disabled:text-[#a1a1aa] disabled:shadow-none disabled:cursor-not-allowed',

    // ---------- badges de status (compartilhado card/tabela) ----------
    badge: 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold whitespace-nowrap flex-shrink-0 border',
    badgeOk: 'bg-[#ecfdf3] text-[#067647] border-[#abefc6]',
    badgeWarn: 'bg-[#fffaeb] text-[#b54708] border-[#fedf89]',
    badgeDot: 'w-1.5 h-1.5 rounded-full',
    badgeDotOk: 'bg-[#17b26a]',
    badgeDotWarn: 'bg-[#f79009]',

    // ---------- cards ----------
    cards: {
        grid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5',
        item: 'bg-white border border-[#e7e7ec] rounded-[12px] p-[22px] overflow-hidden min-w-0 flex flex-col transition-colors hover:border-[#d4d4d8]',
        header: 'flex items-start gap-3 mb-3',
        iconBox: 'w-11 h-11 rounded-[10px] flex items-center justify-center flex-shrink-0',
        info: 'flex-1 min-w-0 pt-0.5',
        name: 'text-[14px] font-semibold text-[#18181b] truncate',
        cardDetail: 'flex items-center gap-1.5 mt-1 text-[12.5px] text-[#71717a] min-w-0',
        platformChip:
            'inline-flex items-center gap-1 text-[11px] font-semibold text-[#71717a] bg-[#f4f4f5] px-1.5 py-0.5 rounded-md w-fit mb-4',
        cardFooter: 'flex flex-wrap items-center gap-1.5 mt-auto pt-3.5 border-t border-[#f0f0f3]',
        connectBtn:
            'flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-gradient-to-b from-[#8b5cf6] to-[#7c3aed] text-white text-[12.5px] font-semibold whitespace-nowrap shadow-[0_1px_4px_rgba(124,58,237,.25)] transition-colors hover:from-[#7c3aed] hover:to-[#6d28d9]',
        configBtn:
            'inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-[#e7e7ec] bg-white text-[12.5px] font-semibold text-[#52525b] whitespace-nowrap transition-colors hover:bg-[#f4f4f7] hover:text-[#18181b]',
        disconnectBtn:
            'w-9 h-9 flex-shrink-0 inline-flex items-center justify-center rounded-lg border border-[#fedf89] bg-white text-[#b54708] transition-colors hover:bg-[#fffaeb]',
        deleteBtn:
            'w-9 h-9 flex-shrink-0 inline-flex items-center justify-center rounded-lg border border-[#e7e7ec] bg-white text-[#a1a1aa] transition-colors hover:text-[#b42318] hover:bg-[#fef3f2] hover:border-[#fecdca]'
    },

    // ---------- tabela ----------
    table: {
        wrap: 'w-full',
        row: 'flex items-center border-b border-[#f0f0f3] last:border-b-0 px-4 py-3.5 transition-colors hover:bg-[#fafafb]',
        iconBox: 'w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0',
        name: 'text-[14px] font-semibold text-[#18181b] truncate',
        platform: 'text-[12.5px] text-[#71717a] truncate',
        detail: 'text-[13.5px] text-[#71717a] truncate',
        actionBtn: 'p-2 rounded-lg text-[#71717a] transition-colors hover:bg-[#f4f4f7] hover:text-[#18181b]',
        actionBtnPrimary: 'p-2 rounded-lg text-[#6d28d9] transition-colors hover:bg-[#ede9fe]',
        actionBtnWarn: 'p-2 rounded-lg text-[#71717a] transition-colors hover:bg-[#fffaeb] hover:text-[#b54708]',
        actionBtnDanger: 'p-2 rounded-lg text-[#a1a1aa] transition-colors hover:bg-[#fef3f2] hover:text-[#b42318]'
    },

    // ---------- skeleton ----------
    skeleton: {
        card: 'bg-white rounded-[12px] border border-[#e7e7ec] p-[22px] animate-pulse',
        avatar: 'w-11 h-11 bg-[#f4f4f5] rounded-[10px] flex-shrink-0',
        line: 'bg-[#f4f4f5] rounded',
        pill: 'bg-[#f4f4f5] rounded-full',
        table: 'w-full bg-white rounded-[12px] border border-[#e7e7ec] p-6 animate-pulse',
        tableHead: 'h-10 bg-[#f4f4f5] rounded-lg mb-4 w-full',
        tableRow: 'h-16 bg-[#fafafb] rounded-lg w-full'
    },

    // ---------- estado de erro ----------
    error: {
        wrap: 'flex flex-col items-center justify-center py-14 px-4 animate-in fade-in slide-in-from-bottom-2 duration-300',
        card: 'bg-white border border-[#fecdca] rounded-[14px] p-8 max-w-md w-full text-center',
        iconBox: 'w-16 h-16 rounded-full bg-[#fef3f2] flex items-center justify-center text-[#b42318] mx-auto mb-4',
        title: 'text-[15px] font-bold text-[#18181b] mb-2',
        desc: 'text-[13px] text-[#71717a] leading-[1.5] mb-6',
        retryBtn:
            'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[#fecdca] text-[#b42318] text-[13px] font-semibold bg-white transition-colors hover:bg-[#fef3f2] mx-auto'
    },

    // ---------- estado vazio ----------
    empty: {
        container: 'bg-white rounded-[14px] border border-dashed border-[#e7e7ec] px-6 py-14 flex flex-col items-center text-center',
        iconBox: 'w-16 h-16 rounded-[14px] bg-[#f4f4f5] text-[#a1a1aa] flex items-center justify-center mb-4',
        title: 'text-[15px] font-bold text-[#18181b]',
        desc: 'mt-1.5 text-[13px] text-[#71717a] leading-[1.5] max-w-sm mx-auto'
    },

    // ---------- modal de confirmação ----------
    modal: {
        overlay: 'fixed inset-0 bg-[#18181b]/40 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200',
        card: 'bg-white rounded-[14px] p-6 max-w-sm w-full mx-4 border border-[#e7e7ec] shadow-xl',
        iconBox: 'mx-auto w-12 h-12 rounded-[12px] flex items-center justify-center mb-4',
        iconBoxDanger: 'bg-[#fef3f2] text-[#b42318]',
        iconBoxWarn: 'bg-[#fffaeb] text-[#b54708]',
        title: 'text-[15px] font-bold text-[#18181b] text-center',
        desc: 'text-[13px] text-[#71717a] leading-[1.5] mt-2 text-center',
        actions: 'flex flex-col-reverse sm:flex-row gap-2.5 mt-6',
        cancelBtn: 'flex-1 h-10 px-4 text-[13px] font-semibold text-[#52525b] bg-[#f4f4f5] rounded-lg transition-colors hover:bg-[#e7e7ec]',
        confirmDangerBtn: 'flex-1 h-10 px-4 text-[13px] font-semibold text-white bg-[#b42318] rounded-lg transition-colors hover:bg-[#96201a]',
        confirmWarnBtn: 'flex-1 h-10 px-4 text-[13px] font-semibold text-white bg-[#b54708] rounded-lg transition-colors hover:bg-[#93370d]'
    },

    // ---------- botões genéricos ----------
    ghostButton:
        'inline-flex items-center justify-center gap-2 px-4 py-[9px] rounded-lg border border-[#e7e7ec] bg-white text-[13px] font-semibold text-[#52525b] whitespace-nowrap transition-colors hover:bg-[#f4f4f7] hover:text-[#18181b] disabled:opacity-50 disabled:cursor-not-allowed'
};

export default CANAIS_COMPONENT_DESIGN;
